import * as React from 'react';
import {
    Box,
    Button,
    Dialog,
    Stack,
    TablePagination,
    Tooltip,
    Typography,
} from '@mui/material';
import Filter from 'component/filter';
import colors from 'assets/theme/base/colors';
import borders from 'assets/theme/base/borders';
import FlexibleTable from 'component/flexible-table';
import { useEffect, useState } from 'react';
import { RowDataType, TableMetaData } from 'types/common/common';
import { useAppDispatch } from 'hooks/redux';
import { ReactComponent as Edit } from 'assets/images/staff-user/edit.svg';
import { ReactComponent as Delete } from 'assets/images/staff-user/delete.svg';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import { openConfirmationDialogs } from 'store/slices/confirmation-dialog/confirmation-dialog-slice';
import FilterCards from 'pages/cards-sphere/filter-sphere/index';
import AddCardsSphere from 'pages/cards-sphere/add-cards-sphere';
import EditCardsSphere from 'pages/cards-sphere/edit-cards-sphere';
import {
    useSphereImageUpdateMutation,
    useSphereBackgroundImageUpdateMutation,
    useSphereImageDeleteMutation,
} from 'store/slices/cards/sphere/add-sphere-images';
import {
    CardsSphereData,
    CardsSphereListRequest,
} from 'types/card/card-sphere-list';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import pxToRem from 'assets/theme/function/px-to-rem';
import AddImages from 'component/add-images';
import { useLazySphereListQuery } from 'store/slices/cards/sphere/sphere-list';
import { useSphereActiveMutation } from 'store/slices/cards/sphere/active-sphere';
import { ImageTypeEnum } from 'types/card/card';
import { getStatusColor } from 'component/status-style';
import { StyledTableRow } from 'component/tabel-pagination-style';
import { API_ENDPOINT_URL } from 'constant';
import ViewPhoto from 'component/view-photo';
import { UrlObject } from 'pages/card';
import { openBackdrop } from 'store/slices/backdrop/backdrop-slice';

export const initialListData: CardsSphereListRequest = {
    page: 1,
    pageSize: 10,
    sort: 'code',
};

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'img',
        position: 1,
        title: 'Image',
        isSortable: false,
    },
    {
        key: 'title',
        position: 2,
        title: 'Name',
        isSortable: false,
    },
    {
        key: 'code',
        position: 3,
        title: 'Code Sphere',
        isSortable: true,
    },

    {
        key: 'createdDate',
        position: 4,
        title: 'Creation Data',
        isSortable: true,
        type: 'date',
        format: 'DD.MM.YYYY HH:mm',
    },
    {
        key: 'status',
        position: 5,
        title: 'Status',
        isSortable: false,
    },

    {
        key: 'action',
        position: 6,
        title: 'Action',
        isSortable: false,
    },
];

const CardSphere: React.FC = () => {
    const [openAddCardsSphereDialog, setOpenAddCardsSphereDialog] =
        React.useState(false);
    const [tableMetaDataList, setTableMetaDataList] = useState<TableMetaData[]>(
        defaultTableStaffDataList,
    );
    const [openEditSphereDialog, setOpenEditSphereDialog] =
        React.useState(false);
    const [openImgDialog, setOpenImgDialog] = React.useState(false);
    const [openBackgroundImgDialog, setOpenBackgroundImgDialog] =
        React.useState(false);
    const [dataSphere, setDataSphere] = React.useState<CardsSphereData>();
    const [dataSphereRequestList, setDataSphereRequestList] =
        React.useState<CardsSphereListRequest>(initialListData);
    const [pagesCount, setPagesCount] = useState<number>(0);
    const [page, setPage] = useState<number>(initialListData?.page);
    const [pageSize, setPageSize] = useState<number>(initialListData?.pageSize);
    const [sort, setSort] = useState<string>(initialListData.sort);
    const [tableData, setTableData] = useState<RowDataType[]>([]);
    const [viewPhotoUrl, setViewPhotoUrl] = useState<UrlObject | null>(null);

    const [
        sphereImagesUpdateApi,
        {
            error: imagesError,
            isError: imagesIsError,
            isSuccess: imagesIsSuccess,
        },
    ] = useSphereImageUpdateMutation();
    const [
        sphereBackgroundImagesUpdateApi,
        {
            error: imagesBgError,
            isError: imagesBgIsError,
            isSuccess: imagesBgIsSuccess,
        },
    ] = useSphereBackgroundImageUpdateMutation();

    const [
        sphereDeleteApi,
        {
            error: deleteError,
            isError: deleteIsError,
            isSuccess: deleteIsSuccess,
        },
    ] = useSphereImageDeleteMutation();

    const [getSphereList, { data, isFetching }] = useLazySphereListQuery();

    const [
        statusActiveApi,
        { error: activeError, isError: activeIsError, isSuccess },
    ] = useSphereActiveMutation();

    const dispatch = useAppDispatch();

    const createNewSphere = () => {
        setOpenAddCardsSphereDialog(true);
    };
    const updateImages = (images: FormData) => {
        sphereImagesUpdateApi({
            cardSphereId: dataSphere?.id,
            image: images,
        });
    };

    const deleteSphere = ({ id }: CardsSphereData) => {
        if (id) {
            sphereDeleteApi(id);
        }
    };

    const getSphereListFunction = () => {
        getSphereList({
            ...dataSphereRequestList,
            sort: sort,
            page: page,
            pageSize: pageSize,
        });
    };

    const updateBackgroundImages = (images: FormData) => {
        sphereBackgroundImagesUpdateApi({
            cardSphereId: dataSphere?.id,
            image: images,
        });
    };

    const handelClickEditSphere = (fileId: CardsSphereData) => {
        setOpenEditSphereDialog(true);
        setDataSphere(fileId);
    };

    const openPictureDialogFunc = (params: CardsSphereData) => {
        setDataSphere(params);
        setOpenImgDialog(true);
    };

    const openBackgroundPictureDialogFun = (params: CardsSphereData) => {
        setDataSphere(params);
        setOpenBackgroundImgDialog(true);
    };

    const handleFilterData = (currentPage = 0) => {
        setPage(currentPage + 1);
    };

    const handelClickStatus = (productActiveId: string) => {
        statusActiveApi(productActiveId);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    useEffect(() => {
        setPagesCount(
            data && data?.data.length > 0 ? Number(data?.rowsCount) : 0,
        );
        const tableData: RowDataType[] = data
            ? data.data.map((fullInfo) => {
                  return {
                      ...fullInfo,
                      status: (
                          <Box>
                              <Button
                                  variant={'contained'}
                                  size={'small'}
                                  sx={{
                                      bgcolor: getStatusColor(fullInfo.status),
                                      pointerEvents:
                                          fullInfo.status !== 'draft'
                                              ? 'none'
                                              : 'auto',
                                  }}
                                  onClick={() => {
                                      dispatch(
                                          openConfirmationDialogs({
                                              textInfo:
                                                  'Are You Sure Want To Active Sphere',
                                              handleConfirm: () =>
                                                  handelClickStatus(
                                                      fullInfo.id,
                                                  ),
                                          }),
                                      );
                                  }}
                              >
                                  {fullInfo.status}
                              </Button>
                          </Box>
                      ),
                      img: (
                          <>
                              {fullInfo?.fileId ? (
                                  <Box
                                      component="img"
                                      src={`${API_ENDPOINT_URL}/file/file/${fullInfo?.fileId}?source=${ImageTypeEnum.SPHERE}`}
                                      alt=""
                                      onClick={() => {
                                          setViewPhotoUrl({
                                              url: `${API_ENDPOINT_URL}/file/file/${fullInfo?.fileId}?source=${ImageTypeEnum.SPHERE}`,
                                              title: fullInfo.title,
                                          });
                                      }}
                                      sx={{
                                          cursor: 'pointer',
                                          width: 100,
                                          height: 80,
                                          objectFit: 'contain',
                                          objectPosition: 'top',
                                          borderRadius: borders.borderRadius.md,
                                      }}
                                  />
                              ) : (
                                  <Typography variant={'h5'}>-</Typography>
                              )}
                          </>
                      ),
                      action: (
                          <>
                              <Box
                                  display={'flex'}
                                  justifyContent={'center'}
                                  gap={2}
                              >
                                  {fullInfo.status === 'draft' && (
                                      <Tooltip title="Delete">
                                          <Delete
                                              cursor="pointer"
                                              onClick={() => {
                                                  dispatch(
                                                      openConfirmationDialogs({
                                                          textInfo:
                                                              'Are You Sure Want To Delete Sphere',
                                                          handleConfirm:
                                                              deleteSphere,
                                                          parameters: {
                                                              id: fullInfo.id,
                                                          },
                                                      }),
                                                  );
                                              }}
                                          />
                                      </Tooltip>
                                  )}
                                  <Tooltip title="Edit">
                                      <Edit
                                          cursor="pointer"
                                          onClick={() =>
                                              handelClickEditSphere({
                                                  fileId: fullInfo.fileId,
                                                  id: fullInfo.id,
                                              })
                                          }
                                      />
                                  </Tooltip>
                                  <Tooltip title="Add Photo">
                                      <AddPhotoAlternateIcon
                                          cursor="pointer"
                                          onClick={() =>
                                              openPictureDialogFunc({
                                                  fileId: fullInfo.fileId,
                                                  id: fullInfo.id,
                                                  imageType:
                                                      ImageTypeEnum.SPHERE,
                                              })
                                          }
                                      />
                                  </Tooltip>
                                  <Tooltip title="Add Photo Bg">
                                      <AddToPhotosIcon
                                          cursor="pointer"
                                          onClick={() =>
                                              openBackgroundPictureDialogFun({
                                                  fileId: fullInfo.backgroundFileId,
                                                  id: fullInfo.id,
                                                  imageType:
                                                      ImageTypeEnum.SPHERE,
                                              })
                                          }
                                      />
                                  </Tooltip>
                              </Box>
                          </>
                      ),
                      rowComponent: StyledTableRow,
                  };
              })
            : [];
        setTableData(tableData);
    }, [data]);

    useEffect(() => {
        if (dataSphereRequestList) {
            getSphereListFunction();
        }
    }, [dataSphereRequestList, page, pageSize, sort]);

    useEffect(() => {
        if (
            imagesIsSuccess ||
            imagesBgIsSuccess ||
            deleteIsSuccess ||
            isSuccess
        ) {
            setOpenImgDialog(false);
            setOpenBackgroundImgDialog(false);
            getSphereList(dataSphereRequestList);
        }
    }, [imagesIsSuccess, imagesBgIsSuccess, deleteIsSuccess, isSuccess]);

    useEffect(() => {
        dispatch(openBackdrop({ open: isFetching }));
    }, [isFetching]);

    useEffect(() => {
        const error =
            imagesError || imagesBgError || deleteError || activeError;

        if (error && 'data' in error && error.data?.message) {
            dispatch(
                openSnackbar({
                    message: error.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [imagesIsError, imagesBgIsError, deleteIsError, activeIsError]);

    return (
        <Box>
            <Filter>
                <FilterCards
                    setDataSphereRequestList={setDataSphereRequestList}
                />
            </Filter>
            <Box
                bgcolor={colors.secondary.light}
                borderRadius={borders.borderRadius.md}
                mt={pxToRem(7)}
            >
                <FlexibleTable
                    tableMetaDataList={tableMetaDataList}
                    setTableMetaDataList={setTableMetaDataList}
                    tableDataList={tableData}
                    sort={sort}
                    setSort={setSort}
                    createNewFun={createNewSphere}
                    buttonName={'Creat New Sphere'}
                />
                {pagesCount > 0 && (
                    <Stack spacing={2} alignItems={'end'}>
                        <TablePagination
                            rowsPerPageOptions={[10, 30, 50]}
                            component="div"
                            count={pagesCount}
                            rowsPerPage={pageSize}
                            page={page - 1}
                            labelRowsPerPage={'Number of rows'}
                            slotProps={{
                                select: {
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    MenuProps: {
                                        disablePortal: true,
                                        slotProps: {
                                            paper: {
                                                sx: {
                                                    backgroundColor:
                                                        colors.secondary.main,
                                                    padding: 0,
                                                },
                                            },
                                        },
                                    },
                                    native: false,
                                },
                            }}
                            onPageChange={(_, page) => handleFilterData(page)}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Stack>
                )}
            </Box>
            <AddCardsSphere
                refetch={getSphereListFunction}
                openDialog={openAddCardsSphereDialog}
                setOpenDialog={setOpenAddCardsSphereDialog}
            />

            {dataSphere?.id && (
                <>
                    <Dialog
                        open={openImgDialog}
                        onClose={() => {
                            setOpenImgDialog(false);
                        }}
                    >
                        <AddImages
                            imageUrl={[
                                dataSphere.fileId
                                    ? `${API_ENDPOINT_URL}/file/file/${dataSphere.fileId}?source=${dataSphere.imageType}`
                                    : undefined,
                            ]}
                            setOpenImgDialog={setOpenImgDialog}
                            onSubmitFun={updateImages}
                            dialogTitle={'SPHERE IMAGE'}
                        />
                    </Dialog>

                    <Dialog
                        open={openBackgroundImgDialog}
                        onClose={() => {
                            setOpenBackgroundImgDialog(false);
                        }}
                    >
                        <AddImages
                            imageUrl={[
                                dataSphere.fileId
                                    ? `${API_ENDPOINT_URL}/file/file/${dataSphere.fileId}?source=${dataSphere.imageType}`
                                    : undefined,
                            ]}
                            setOpenImgDialog={setOpenBackgroundImgDialog}
                            onSubmitFun={updateBackgroundImages}
                            dialogTitle={'SPHERE BACKGROUND IMAGES'}
                        />
                    </Dialog>

                    <Dialog
                        open={openEditSphereDialog}
                        onClose={() => {
                            setOpenEditSphereDialog(false);
                        }}
                    >
                        <EditCardsSphere
                            refetch={getSphereListFunction}
                            sphereId={dataSphere.id}
                            setOpenEditSphereDialog={setOpenEditSphereDialog}
                        />
                    </Dialog>
                </>
            )}

            {viewPhotoUrl && (
                <ViewPhoto
                    open={Boolean(viewPhotoUrl)}
                    setOpen={() => setViewPhotoUrl(null)}
                    imageUrl={viewPhotoUrl}
                />
            )}
        </Box>
    );
};

export default CardSphere;
